import { useState, useCallback } from 'react';
import {
  useCategoriesWithTransactionCount,
  useRootCategories,
  useAddCategory,
  useUpdateCategory,
  useDeleteCategory,
  type CategoryWithTransactionCount,
} from '../../hooks';
import type { Category } from '../../types';
import { CategoryForm } from './CategoryForm';
import { ConfirmDialog } from './ConfirmDialog';

interface CategoriesManagerProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface CategoryRowProps {
  category: CategoryWithTransactionCount;
  depth: number;
  allCategories: CategoryWithTransactionCount[];
  onEdit: (category: Category) => void;
  onDelete: (category: CategoryWithTransactionCount) => void;
}

const PRESET_COLORS = [
  '#4CAF50',
  '#2196F3',
  '#9C27B0',
  '#FF9800',
  '#607D8B',
  '#E91E63',
  '#00BCD4',
  '#795548',
  '#3F51B5',
  '#8BC34A',
  '#FF5722',
  '#9E9E9E',
];

const COLOR_NAMES: Record<string, string> = {
  '#4CAF50': 'Verde',
  '#2196F3': 'Azul',
  '#9C27B0': 'Morado',
  '#FF9800': 'Naranja',
  '#607D8B': 'Gris azulado',
  '#E91E63': 'Rosa',
  '#00BCD4': 'Cian',
  '#795548': 'Cafe',
  '#3F51B5': 'Indigo',
  '#8BC34A': 'Verde claro',
  '#FF5722': 'Naranja oscuro',
  '#9E9E9E': 'Gris',
};

function CategoryRow({
  category,
  depth,
  allCategories,
  onEdit,
  onDelete,
}: CategoryRowProps) {
  const children = allCategories.filter((c) => c.parentId === category.id);
  const paddingLeft = depth * 24 + 16;

  return (
    <>
      <div
        className="flex items-center justify-between py-3 px-4 border-b border-gray-100 hover:bg-gray-50 gap-2"
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 flex-wrap">
          {depth > 0 && <span className="text-gray-400 text-sm">└</span>}
          <span
            className="w-4 h-4 rounded-full flex-shrink-0"
            style={{ backgroundColor: category.color }}
          />
          <span className="text-near-black break-words">
            {category.icon && <span className="mr-1">{category.icon}</span>}
            {category.name}
          </span>
          <span className="text-xs text-mid-gray bg-gray-100 px-2 py-0.5 rounded-full">
            {category.transactionCount}{' '}
            {category.transactionCount === 1 ? 'transaccion' : 'transacciones'}
          </span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={() => onEdit(category)}
            className="p-2 text-mid-gray hover:text-near-black transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={`Editar ${category.name}`}
          >
            ✏️
          </button>
          <button
            onClick={() => onDelete(category)}
            disabled={category.id === 'sin-categoria'}
            className="p-2 text-mid-gray hover:text-red-600 transition disabled:opacity-30 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={`Eliminar ${category.name}`}
            title={
              category.id === 'sin-categoria'
                ? 'La categoria Sin Categoria no se puede eliminar'
                : 'Eliminar categoria'
            }
          >
            🗑️
          </button>
        </div>
      </div>
      {children.map((child) => (
        <CategoryRow
          key={child.id}
          category={child}
          depth={depth + 1}
          allCategories={allCategories}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </>
  );
}

interface CategoryFormData {
  name: string;
  icon: string;
  color: string;
  parentId: string | null;
}

export function CategoriesManager({
  onSuccess,
  onError,
}: CategoriesManagerProps) {
  const categoriesWithCount = useCategoriesWithTransactionCount();
  const rootCategories = useRootCategories();
  const addCategory = useAddCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteConfirm, setDeleteConfirm] =
    useState<CategoryWithTransactionCount | null>(null);

  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    icon: '',
    color: PRESET_COLORS[0],
    parentId: null,
  });

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      icon: '',
      color: PRESET_COLORS[0],
      parentId: null,
    });
    setIsAddingNew(false);
    setEditingCategory(null);
  }, []);

  const handleStartAdd = useCallback(() => {
    setEditingCategory(null);
    setFormData({
      name: '',
      icon: '',
      color: PRESET_COLORS[0],
      parentId: null,
    });
    setIsAddingNew(true);
  }, []);

  const handleStartEdit = useCallback((category: Category) => {
    setIsAddingNew(false);
    setEditingCategory(category);
    setFormData({
      name: category.name,
      icon: category.icon ?? '',
      color: category.color,
      parentId: category.parentId,
    });
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!formData.name.trim()) {
        onError('El nombre es requerido');
        return;
      }

      try {
        if (editingCategory) {
          updateCategory(editingCategory.id, {
            name: formData.name.trim(),
            icon: formData.icon.trim() || undefined,
            color: formData.color,
            parentId: formData.parentId,
          });
          onSuccess('Categoria actualizada');
        } else {
          addCategory({
            name: formData.name.trim(),
            icon: formData.icon.trim() || undefined,
            color: formData.color,
            parentId: formData.parentId,
          });
          onSuccess('Categoria creada');
        }
        resetForm();
      } catch {
        onError('Error al guardar la categoria');
      }
    },
    [
      formData,
      editingCategory,
      addCategory,
      updateCategory,
      onSuccess,
      onError,
      resetForm,
    ]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteConfirm) return;

    try {
      deleteCategory(deleteConfirm.id);
      onSuccess('Categoria eliminada');
      setDeleteConfirm(null);
    } catch {
      onError('Error al eliminar la categoria');
    }
  }, [deleteConfirm, deleteCategory, onSuccess, onError]);

  const rootCategoriesWithCount = categoriesWithCount.filter(
    (c) => c.parentId === null
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-near-black">Categorias</h3>
        <button
          onClick={handleStartAdd}
          className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-brand-secondary transition min-h-[44px]"
        >
          + Nueva categoria
        </button>
      </div>

      {(isAddingNew || editingCategory) && (
        <CategoryForm
          formData={formData}
          onChange={setFormData}
          onSubmit={handleSubmit}
          onCancel={resetForm}
          isEditing={!!editingCategory}
          rootCategories={rootCategories}
          editingCategoryId={editingCategory?.id}
          presetColors={PRESET_COLORS}
          colorNames={COLOR_NAMES}
        />
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {rootCategoriesWithCount.length === 0 ? (
          <div className="p-8 text-center text-mid-gray">
            No hay categorias. Crea una nueva para empezar.
          </div>
        ) : (
          rootCategoriesWithCount.map((category) => (
            <CategoryRow
              key={category.id}
              category={category}
              depth={0}
              allCategories={categoriesWithCount}
              onEdit={handleStartEdit}
              onDelete={setDeleteConfirm}
            />
          ))
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title="Eliminar categoria"
        message={
          <>
            <p className="text-mid-gray mb-4">
              ¿Estas seguro de que quieres eliminar la categoria &quot;
              {deleteConfirm?.name}&quot;?
            </p>
            {deleteConfirm && deleteConfirm.transactionCount > 0 && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                <span className="text-amber-600 text-lg">⚠️</span>
                <div className="text-sm text-amber-800">
                  Esta categoria tiene {deleteConfirm.transactionCount}{' '}
                  {deleteConfirm.transactionCount === 1
                    ? 'transaccion que sera movida'
                    : 'transacciones que seran movidas'}{' '}
                  a &quot;Sin Categoria&quot;.
                </div>
              </div>
            )}
          </>
        }
        confirmLabel="Eliminar"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        variant="danger"
      />
    </div>
  );
}
